import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });    this.#installFade();
    customElements.whenDefined('slideshow-component').then(() => this.isConnected && this.#installFade());
  }

  // FlakkoShop: crossfade when a slide is picked by tap (dots/thumbnails/arrows). Swipes stay native.
  #installFade() {
    const ss = this.querySelector('slideshow-component');
    if (!ss || ss.__fwFade || typeof ss.select !== 'function') return;
    const orig = ss.select.bind(ss);
    const reduce = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
    ss.__fwFade = true;
    ss.select = (input, event, options = {}) => {
      if (!event || options.animate === false || reduce()) return orig(input, event, options);
      const cur = ss.slides?.[ss.current];
      const img = cur && [...cur.querySelectorAll('img')].find((i) => i.offsetWidth > 0);
      if (img) {
        const g = this.getBoundingClientRect(), r = img.getBoundingClientRect();
        if (getComputedStyle(this).position === 'static') this.style.position = 'relative';
        const ov = img.cloneNode(false);
        ov.removeAttribute('loading'); ov.removeAttribute('id');
        ov.setAttribute('aria-hidden', 'true');
        Object.assign(ov.style, { position: 'absolute', margin: '0', left: r.left - g.left + 'px', top: r.top - g.top + 'px', width: r.width + 'px', height: r.height + 'px', maxWidth: 'none', objectFit: getComputedStyle(img).objectFit, zIndex: '5', pointerEvents: 'none', willChange: 'opacity', transform: 'translateZ(0)' });
        this.appendChild(ov);
        const res = orig(input, event, { ...options, animate: false });
        ov.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 350, easing: 'ease-in-out', fill: 'forwards' }).finished.finally(() => ov.remove());
        return res;
      }
      return orig(input, event, options);
    };
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // FlakkoShop: skip the swap when the gallery would be identical (e.g. size-only change),
        // and when it does change (e.g. color), keep height and scroll position stable.
        const sig = (el) => {
          const ss = el.querySelector('slideshow-component');
          const imgs = [...el.querySelectorAll('img')].map((i) => (i.getAttribute('src') || '').split('?')[0]);
          return (ss ? ss.getAttribute('initial-slide') || '' : '') + '|' + imgs.join(',');
        };
        if (sig(newMediaGallery) === sig(this)) return;
        const y = window.scrollY;
        const h = this.offsetHeight;
        newMediaGallery.style.minHeight = h + 'px';
        const fade = !matchMedia('(prefers-reduced-motion: reduce)').matches && this.offsetParent;
        if (fade) {
          const top = this.offsetTop, left = this.offsetLeft, w = this.offsetWidth;
          this.#controller.abort();
          this.before(newMediaGallery);
          Object.assign(this.style, { position: 'absolute', top: top + 'px', left: left + 'px', width: w + 'px', margin: '0', zIndex: '5', pointerEvents: 'none', willChange: 'opacity' });
          this.setAttribute('aria-hidden', 'true');
          this.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 350, easing: 'ease-in-out', fill: 'forwards' }).finished.finally(() => this.remove());
        } else {
          this.replaceWith(newMediaGallery);
        }
        if (Math.abs(window.scrollY - y) > 1) window.scrollTo({ top: y, behavior: 'instant' });
        const release = () => { newMediaGallery.style.minHeight = ''; };
        const first = newMediaGallery.querySelector('img');
        if (first && !first.complete) first.addEventListener('load', release, { once: true }); else requestAnimationFrame(release);
        setTimeout(release, 1500);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
